import React from 'react';
import PropTypes from 'prop-types';
import Notice from '../Notice';
import AppointmentTimeBlock from '../AppointmentTimeBlock';
import NewOrExistingPatient from '../NewOrExistingPatient';
import ProcedureDropdownBlock from '../ProcedureDropdownBlock';
import { FormRow } from '@zocdoc-frontend-common/styles-grid';
import { APPOINTMENT_INFO_STRINGS } from '../../pages/AppointmentInfo/constants';
import { APPOINTMENT_TIME_STRINGS } from '../AppointmentTimeBlock/constants';
import styled, { keyframes } from 'styled-components';
import { breakpoint, GridSize } from '@zocdoc-frontend-common/styles-grid';
import Alert from '@zocdoc-frontend-common/assets-icons/Alert';
import { DEPRECATED } from '@zocdoc-mezzanine/foundations-colors';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import { Spinner } from '@zocdoc-frontend-common/components-spinner';

export const AppointmentInfoForm = props => {
    const {
        appointmentPickerRef,
        directoryId,
        formattedAppointmentTime,
        hasAppointmentTimeError,
        insuranceCarrierId,
        insurancePlanId,
        isNewPatient,
        isReschedule,
        locationId,
        onEditClick,
        onRadioChange,
        onTimeSlotClicked,
        procedureId,
        procedureMayNotBeCovered,
        professionalName,
        providerId,
        showEmergencyNotice,
        showTimesGrid,
        updatingIsExistingPatient,
    } = props;

    const renderAppointmentTimeNotice = () => {
        if (showTimesGrid) {
            return (
                <AppointmentTimeNotice showNotice={hasAppointmentTimeError}>
                    {APPOINTMENT_TIME_STRINGS.NoLongerAvailable}
                </AppointmentTimeNotice>
            );
        }

        /* eslint-disable @zocdoc-frontend-common/no-nbsp */
        return (
            <AppointmentTimeNotice showNotice={hasAppointmentTimeError}>
                {APPOINTMENT_TIME_STRINGS.Oops}
                &nbsp;&nbsp;
                {APPOINTMENT_TIME_STRINGS.NoLongerAvailable}
            </AppointmentTimeNotice>
        );
        /* eslint-enable @zocdoc-frontend-common/no-nbsp */
    };

    return (
        <React.Fragment>
            <FormRow>
                <FormLabel>
                    {APPOINTMENT_INFO_STRINGS.VisitReasonPrompt}
                </FormLabel>
                <ProcedureDropdownBlock
                    procedureId={procedureId}
                    providerId={providerId}
                    showEmergencyNotice={showEmergencyNotice}
                />
                <Notice
                    data-test="procedure-may-not-be-covered-notice"
                    showNotice={procedureMayNotBeCovered}
                >
                    {APPOINTMENT_INFO_STRINGS.ProcedureMayNotBeCovered}
                </Notice>
            </FormRow>

            <FormRow>
                <FormLabel>
                    {`Have you seen ${professionalName} before?`}
                </FormLabel>
                <NewOrExistingPatientWithSpinner
                    isNewPatient={isNewPatient}
                    onRadioChange={onRadioChange}
                    updatingIsExistingPatient={updatingIsExistingPatient}
                />
            </FormRow>

            <div ref={appointmentPickerRef}>
                <FormLabel>
                    {APPOINTMENT_INFO_STRINGS.AppointmentTime}
                </FormLabel>
                <AppointmentTimeBlock
                    directoryId={directoryId}
                    formattedAppointmentTime={formattedAppointmentTime}
                    insuranceCarrierId={insuranceCarrierId}
                    insurancePlanId={insurancePlanId}
                    isNewPatient={isNewPatient}
                    isReschedule={isReschedule}
                    locationId={locationId}
                    onEditClick={onEditClick}
                    onTimeSlotClicked={onTimeSlotClicked}
                    procedureId={procedureId}
                    providerId={providerId}
                    showTimesGrid={showTimesGrid}
                />
                {renderAppointmentTimeNotice()}
            </div>
        </React.Fragment>
    );
};

AppointmentInfoForm.propTypes = {
    appointmentPickerRef: PropTypes.object,
    formattedAppointmentTime: PropTypes.string.isRequired,
    isNewPatient: PropTypes.bool.isRequired,
    isReschedule: PropTypes.bool,
    locationId: PropTypes.string.isRequired,
    onEditClick: PropTypes.func,
    onRadioChange: PropTypes.func.isRequired,
    onTimeSlotClicked: PropTypes.func.isRequired,
    procedureId: PropTypes.string,
    procedureMayNotBeCovered: PropTypes.bool,
    professionalName: PropTypes.string.isRequired,
    providerId: PropTypes.string.isRequired,
    showEmergencyNotice: PropTypes.bool,
    hasAppointmentTimeError: PropTypes.bool.isRequired,
    updatingIsExistingPatient: PropTypes.bool,
};

export const NewOrExistingPatientWithSpinner = ({
    updatingIsExistingPatient,
    isNewPatient,
    alwaysShowVertical = false,
    onRadioChange,
}) => (
    <NewOrExistingPatientContainer
        updatingIsExistingPatient={updatingIsExistingPatient}
    >
        <NewOrExistingPatient
            alwaysShowVertical={alwaysShowVertical}
            isNewPatient={isNewPatient}
            onRadioChange={onRadioChange}
        />

        {updatingIsExistingPatient && (
            <NewOrExistingPatientUpdating data-test="new-existing-spinner">
                <Spinner size={30} />
            </NewOrExistingPatientUpdating>
        )}
    </NewOrExistingPatientContainer>
);

const FormLabel = styled.div`
    padding-bottom: ${GridSize}px;
    font: 15px/24px ${FontFamily.SharpSansSemibold};
`;

const NoticeWrapper = styled.div`
    display: flex;
    flex-direction: row;
`;

const AlertIconWrapper = styled.span`
    min-width: 16px;
    height: 0;
    margin: 2px 12px 0 0;
`;

const NoticeText = styled.span`
    font: 13px/24px ${FontFamily.SharpSansSemibold};

    ${breakpoint.medium} {
        font-size: 15px;
    }
`;

const NewOrExistingPatientContainer = styled.div`
    position: relative;
    pointer-events: ${p => (p.updatingIsExistingPatient ? 'none' : 'auto')};
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// top, left, right and bottom set to 1px so we do not
// cover the borders of the underlying component with this overlay
const NewOrExistingPatientUpdating = styled.div`
    position: absolute;
    top: 1px;
    left: 1px;
    right: 1px;
    bottom: 1px;
    background-color: rgba(255, 255, 255, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    animation: 0.2s ${fadeIn} linear 1;
`;

export const AppointmentTimeNotice = ({
    showNotice,
    className,
    children,
    onClick,
}) => {
    return (
        <Notice
            className={className}
            onClick={onClick}
            isError
            showNotice={showNotice}
        >
            <NoticeWrapper
                data-test="appointment-time-notice"
                data-uem-id="appointment-time-notice"
            >
                <AlertIconWrapper>
                    <Alert color={DEPRECATED.tertiaryCoral} height={16} />
                </AlertIconWrapper>
                <NoticeText>{children}</NoticeText>
            </NoticeWrapper>
        </Notice>
    );
};
